import React from 'react';
import { cn } from '~/lib/utils';
import { ComposerScrollbar } from './ComposerScrollbar';
import type { ComposerScrollbarProps } from './ComposerScrollbar';
import styles from './Composer.module.css';
import { ComposerTextRow } from './ComposerTextRow';
import type { ComposerTextRowProps } from './ComposerTextRow';
import { ComposerToolbar } from './ComposerToolbar';
import type { ComposerToolbarProps } from './ComposerToolbar';
import type { ChatMenu, ChatTheme, ComposerWidth } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The ceiling the text row grows to, and the card height that produces.
 *
 * `Chat/Long text`'s caption says "grows to the real 220px max"; the artboard draws **230** — a
 * 174px text row over the 56px toolbar — and `ComposerScrollbar`'s `bottom: 62px` inset is derived
 * from 230, so building 220 would leave the scrollbar 10px wrong. 230 is the contract (DECISIONS
 * §2).
 *
 * These are exported as numbers rather than enforced as a CSS `max-height`, and that is deliberate:
 * 230 is the ceiling on how tall the composer grows **from typing**. An attachment tray stacks
 * another 110px on top of it — `Chat/Attachment` is 220 (110 + 54 + 56) and use-case `Founder` is
 * 292 (110 + 126 + 56) — so a literal `max-height: 230px` on the card would clip the two artboards
 * that are legitimately taller than the ceiling. The cap lives where it is actually enforced:
 * `ComposerTextRow`'s height union stops at 174.
 */
export const COMPOSER_TEXT_CEILING = 174;
export const COMPOSER_MAX_HEIGHT = 230;

/**
 * What `disabled` puts on the card besides the 45% opacity.
 *
 * `pointer-events: none` in the stylesheet takes the pointer away and nothing else, which left the
 * keyboard able to Tab into a bar painted at 45%, announced `aria-disabled`, and still press send.
 * `inert` closes that: it takes focus, the pointer and the accessibility tree for the whole subtree
 * at once.
 *
 * It stays even though `ComposerToolbar` now HAS a `disabled` pass-through, because the two are not
 * the same guarantee. The toolbar disables the controls it renders; `inert` covers the ones this
 * card never sees — anything a caller puts in the `toolbar` or attachment slots, including the
 * remove dots inside an `AttachmentCard`, which has no `disabled` prop of its own. Belt and braces
 * is right here: a composer that looks disabled and can still be operated by Tab is the worst of
 * the three possible states.
 *
 * Spread rather than written as a JSX attribute because React 18's DOM typings predate `inert`.
 * The empty string is deliberate: React 18 forwards an unknown attribute only when its value is a
 * string, and `inert=""` is the attribute's own present form in the HTML.
 */
const INERT = { inert: '' };

/** Which of the two anchor geometries each menu hangs from. See the stylesheet for the offsets. */
const ANCHOR_CLASS: Record<ChatMenu, string> = {
  add: styles.anchorTop,
  mention: styles.anchorTop,
  model: styles.anchorModel,
};

/** A popover anchored inside the card, and which of the two anchors it uses. */
export interface ComposerMenu {
  /**
   * `add` and `mention` hang off the card's top edge; `model` hangs off the toolbar. The two
   * geometries are the only thing this discriminates — the surfaces themselves arrive as `node`.
   */
  kind: ChatMenu;
  /** The rendered `AddMenu` / `MentionMenu` / `ModelPickerMenu`. It owns its own width. */
  node: React.ReactNode;
}

export interface ComposerProps {
  /**
   * Pins the card to one of the three widths `Chat/Responsive` proves.
   *
   * Omitted, the card is fluid — `width: 100%`, `min-width: 0` — which is what BUILD-CONTRACT §7
   * asks for and what every row inside it already does. The artboards are drawn at 400 because the
   * panel is 400 wide, not because the composer has a width of its own; `Chat/Responsive`'s whole
   * point is that nothing but the card width and `text width = card width − 32` changes across the
   * three. So the prop exists for the proof and for a caller that really does own a fixed shell,
   * and the default is no width at all rather than 400.
   */
  width?: ComposerWidth;
  /**
   * The in-card attachment strip — a `ComposerAttachmentTray`. It sits above the text row and is
   * a fixed 110px, so adding a chip never moves the text or the toolbar under it.
   */
  attachments?: React.ReactNode;
  /** The input area. Passed through whole; `theme` is overridden by the card's own. */
  input: ComposerTextRowProps;
  /**
   * The bottom bar. `theme` is stripped for the same reason `ComposerToolbar` strips it from its
   * `model` prop: one card, one theme decision, and a second one arriving through a nested object
   * could contradict it.
   */
  toolbar: Omit<ComposerToolbarProps, 'theme'>;
  /** The open popover, if any. Adds nothing to the card but a positioned wrapper. */
  menu?: ComposerMenu;
  /**
   * The custom scrollbar for a text row at its 174px ceiling (`Chat/Long text`). It positions
   * itself absolutely against this card, which is why the card is always `position: relative`.
   */
  scrollbar?: ComposerScrollbarProps;
  /**
   * `Chat/Disabled`, whose caption is the entire spec: "whole bar at 45%". One opacity on
   * the card and **nothing per element** — every child stays painted exactly as it is at rest.
   *
   * Behaviour is a separate question from paint, and the artboard cannot draw it: the card also
   * goes `inert`, so the pointer, the keyboard and the announcement all agree that nothing in here
   * can be operated.
   */
  disabled?: boolean;
  /**
   * The composer is the thing being used, which is what raises its drop shadow.
   *
   * A prop rather than a selector because the caret is not in here: both live hosts lay their
   * `<textarea>` over this card as a sibling, so the card cannot see the focus that matters and the
   * host has to hand it back. Omit it and the shadow still follows anything focusable INSIDE the
   * card — the menus — which is the whole behaviour for a static or read-only composer.
   */
  active?: boolean;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The composer card — the box you type into, and the only shell in v1.5 chat that stacks three
 * rows and anchors three menus.
 *
 * It is a shell and almost nothing else: no padding of its own (every row carries its own), no
 * state, no controls. What it owns is the four things a row cannot own for itself — the ground
 * and hairline and shadow that make the rows read as one card, the `position: relative` the
 * scrollbar and the menus are measured against, the two popover anchors, and the single `opacity`
 * that is
 * the whole of the disabled state.
 *
 * The height is always the exact sum of its rows, never set: 110 (54 + 56) · 134 (78 + 56) ·
 * 158 (102 + 56) · 182 (126 + 56) · 220 (110 + 54 + 56) · 230 (174 + 56) ·
 * 292 (110 + 126 + 56). Every one of those numbers falls out of stacking the parts, which is why
 * there is no height
 * anywhere in this file.
 *
 * Artboards: all 17 `Chat` boards · all 4 `Queue` boards · all 5 `Use case` boards · all 4
 * `Conversation` boards.
 */
export function Composer({
  width,
  attachments,
  input,
  toolbar,
  menu,
  scrollbar,
  disabled = false,
  active = false,
  theme: themeProp,
  className,
}: ComposerProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div
      className={cn(chatThemeClass(theme), styles.card, active && styles.active, disabled && styles.disabled, className)}
      // The one runtime value the card has. Left undefined when `width` is omitted so the
      // stylesheet's `100%` fallback wins and the card stays fluid.
      style={width ? ({ '--cv15-composer-w': `${width}px` } as React.CSSProperties) : undefined}
      // The card is a container, not a control, so there is no `disabled` attribute to set and no
      // role to disable. `aria-disabled` is the truthful thing to say about a group: it announces
      // the state without claiming the card is focusable. `INERT` above is what makes it true for
      // every input modality rather than only for the pointer.
      aria-disabled={disabled || undefined}
      {...(disabled ? INERT : null)}
    >
      {attachments}
      <ComposerTextRow {...input} theme={theme} />
      {/* The card's `disabled` wins over the slot's: a disabled composer cannot contain a live
          toolbar, whatever the caller passed. It is an OR rather than an override so a caller can
          still disable the bar alone — the state where the card is live but there is nothing
          sendable yet. */}
      <ComposerToolbar {...toolbar} disabled={disabled || toolbar.disabled} theme={theme} />
      {/* Absolutely positioned against this card. After the toolbar in the DOM because it is a
          picture of the text row's overflow, and reading order should reach the text first. */}
      {scrollbar ? <ComposerScrollbar {...scrollbar} theme={theme} /> : null}
      {menu ? (
        // Two anchors, not three: `add` and `mention` are drawn at the identical `left: 0;
        // bottom: 118px`, so they share one class and only the model picker gets its own.
        // `data-menu-region`: a press in here keeps the menu open; see `deck15/chat/composer/menuGap`.
        <div className={cn(styles.anchor, ANCHOR_CLASS[menu.kind])} data-menu-region="">
          {menu.node}
        </div>
      ) : null}
    </div>
  );
}
